import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../components/customTable/CustomTable";
import { createMRTColumnHelper } from "material-react-table";
import DateSearchComponentForGiftCardUser from "../../components/customTable/DateSearchComponentForGiftCardUser";
import { Box, IconButton, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import { ContentCopy } from "@mui/icons-material"; // Import the copy icon from MUI

import { giftCardUsedApi } from "../../app/slices/giftCardUser/giftCardUsedSlice";

export default function Used() {
  const dispatch = useDispatch();

  const [queryParams, setQueryParams] = useState({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    // Run API only if we have valid dates
    if (queryParams.startDate && queryParams.endDate) {
      dispatch(giftCardUsedApi(queryParams));
    }
  }, [dispatch, queryParams]);

  const handleSearch = (startDate, endDate) => {
    setQueryParams({ startDate, endDate });
  };

  const sliceResponse = useSelector(
    (state) => state.giftCardUsed.giftCardUsed.data?.used,
  );
  // console.log("sliceResponse: ", sliceResponse);
  const stateLoading = useSelector((state) => state.giftCardUsed.loading);

  const columnHelper = createMRTColumnHelper();

  const columns = [
    columnHelper.accessor("_id", {
      header: "ID",
      size: 30,
      Cell: ({ cell }) => {
        const handleCopy = () => {
          navigator.clipboard.writeText(cell.row.original?._id);
          toast.success("Copied to clipboard!");
        };
        return (
          <Tooltip title={cell.row.original?._id}>
            <IconButton onClick={handleCopy}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor("customerRefNo", {
      header: "customerRefNo",
      size: 150,
      Cell: ({ cell }) => {
        const handleCopy = () => {
          navigator.clipboard.writeText(cell.row.original?.customerRefNo?._id);
          toast.success("Copied to clipboard!");
        };
        return (
          <Tooltip title={cell.row.original?.customerRefNo?._id}>
            <span>
              <IconButton onClick={handleCopy}>
                <ContentCopy />
              </IconButton>
              {cell.row.original?.customerRefNo?.name}
            </span>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor("redeemedBy", {
      header: "Redeemed By",
    }),
    columnHelper.accessor("giftcode", {
      header: "Gift Code",
      size: 150,
      Cell: ({ cell }) => {
        const handleCopy = () => {
          navigator.clipboard.writeText(cell.row.original?.giftcode);
          toast.success("Copied to clipboard!");
        };
        return (
          <Tooltip title={cell.row.original?.giftcode}>
            <span>
              <IconButton onClick={handleCopy}>
                <ContentCopy />
              </IconButton>
              {cell.row.original?.giftcode}
            </span>
          </Tooltip>
        );
      },
    }),

    columnHelper.accessor("orderId", {
      header: "orderId",
      size: 150,
      Cell: ({ cell }) => {
        const handleCopy = () => {
          navigator.clipboard.writeText(cell.row.original?.orderId);
          toast.success("Copied to clipboard!");
        };
        return (
          <Tooltip title={cell.row.original?.orderId}>
            <span>
              <IconButton onClick={handleCopy}>
                <ContentCopy />
              </IconButton>
              {cell.row.original?.orderId}
            </span>
          </Tooltip>
        );
      },
    }),

    columnHelper.accessor("payment_id", {
      header: "payment_id",
      size: 150,
      Cell: ({ cell }) => {
        const handleCopy = () => {
          navigator.clipboard.writeText(cell.row.original?.payment_id);
          toast.success("Copied to clipboard!");
        };
        return (
          <Tooltip title={cell.row.original?.payment_id}>
            <span>
              <IconButton onClick={handleCopy}>
                <ContentCopy />
              </IconButton>
              {cell.row.original?.payment_id}
            </span>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor("transactionId", {
      header: "Transaction Id",
      size: 150,
      Cell: ({ cell }) => {
        const handleCopy = () => {
          navigator.clipboard.writeText(cell.row.original?.transactionId);
          toast.success("Copied to clipboard!");
        };
        return (
          <Tooltip title={cell.row.original?.transactionId}>
            <span>
              <IconButton onClick={handleCopy}>
                <ContentCopy />
              </IconButton>
              {cell.row.original?.transactionId || "N/A"}
            </span>
          </Tooltip>
        );
      },
    }),
    // columnHelper.accessor("name", {
    //     header: "Name",
    // }),
    columnHelper.accessor("phone", {
      header: "Phone",
    }),
    // columnHelper.accessor("email", {
    //     header: "Email",
    // }),
    columnHelper.accessor("amount", {
      header: "Amount",
    }),
    columnHelper.accessor("metal_type", {
      header: "Metal Type",
    }),
    columnHelper.accessor("refinery", {
      header: "Refinery",
    }),
    columnHelper.accessor("redeemed", {
      header: "Redeemed",
      Cell: (cell) =>
        cell.row.original.redeemed ? (
          <span style={{ color: "green" }}>yes</span>
        ) : (
          <span style={{ color: "red" }}>No</span>
        ),
    }),
    columnHelper.accessor("status", {
      header: "status",
      Cell: (cell) => cell.row.original.status,
    }),
    columnHelper.accessor("self", {
      header: "Self",
      Cell: (cell) =>
        cell.row.original.self ? (
          <span style={{ color: "green" }}>yes</span>
        ) : (
          <span style={{ color: "red" }}>No</span>
        ),
    }),
    columnHelper.accessor("createdAt", {
      header: "Created At",
      Cell: (cell) => cell.row.original.createdAt.split("T")[0],
    }),
    // columnHelper.accessor("expiresin", {
    //     header: "Expires In",
    //     Cell: (cell) => cell.row.original.expiresin,
    // }),
  ];

  const flatGiftData = useMemo(() => {
    if (!Array.isArray(sliceResponse)) return [];

    return sliceResponse.map((item) => ({
      _id: item._id,

      giftcode: item.giftcode,
      giftType: item.giftType,
      metal_type: item.metal_type,
      refinery: item.refinery,

      amount: Number(item.amount), // convert string → number (optional)
      status: item.status,
      redeemed: item.redeemed,
      self: item.self,

      customerRefNo: item.customerRefNo?.name || "", // ✅ flattened
      customerRefId: item.customerRefNo?._id || "",

      name: item.name,
      phone: item.phone,

      orderId: item.orderId,
      payment_id: item.payment_id,
      transactionId: item.transactionId,

      expiresin: new Date(item.expiresin).toLocaleDateString(),
      createdAt: new Date(item.createdAt).toLocaleString(),
      updatedAt: new Date(item.updatedAt).toLocaleString(),
    }));
  }, [sliceResponse]);

  return (
    <>
      {/*  */}
      <>
        <DateSearchComponentForGiftCardUser onSearch={handleSearch} />
        <Box mt={2}>
          <CustomTable
            columns={columns}
            data={sliceResponse}
            loading={stateLoading}
            DataForExportAll={flatGiftData}
            permissionKey="GiftCardUser"
          />
        </Box>
      </>
    </>
  );
}
